import { useMutation, useQueryClient } from '@tanstack/react-query';
import { checkInService } from '../../services/checkInService';
import type { CheckInCreate } from '../../models/checkIn.schemas';
import { useToast } from '@/hooks/useToast';

/**
 * Hook for check-in mutations (create only - check-ins are immutable)
 * Provides optimistic updates for key result progress
 *
 * @returns Mutation function for creating check-ins
 */
export function useCheckInMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();

  /**
   * Create check-in mutation
   * Check-ins are immutable, so only create is supported
   */
  const createCheckIn = useMutation({
    mutationFn: ({ keyResultId, data }: { keyResultId: string; data: CheckInCreate }) =>
      checkInService.create(keyResultId, data),
    onMutate: async ({ keyResultId, data }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['checkIns', keyResultId] });
      await queryClient.cancelQueries({ queryKey: ['keyResults'] });

      // Get previous data for rollback
      const previousCheckIns = queryClient.getQueryData(['checkIns', keyResultId]);
      const previousKeyResults = queryClient.getQueriesData({ queryKey: ['keyResults'] });

      // Optimistically add check-in to cache
      queryClient.setQueryData(['checkIns', keyResultId], (old: any) => {
        if (!old || !Array.isArray(old)) return [data];
        // Add to beginning since check-ins are ordered by date desc
        return [{ ...data, id: 'temp-' + Date.now(), createdAt: new Date() }, ...old];
      });

      // Optimistically update key result progress
      queryClient.setQueriesData({ queryKey: ['keyResults'] }, (old: any) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((kr: any) => {
          // Find the key result being updated based on check-ins query
          const checkIns = queryClient.getQueryData(['checkIns', kr.id]);
          if (checkIns && Array.isArray(checkIns) && checkIns.length > 0) {
            // This is the key result we're updating
            return {
              ...kr,
              currentValue: data.newValue,
              checkInCount: kr.checkInCount + 1,
              lastCheckInDate: data.checkInDate || new Date(),
            };
          }
          return kr;
        });
      });

      return { previousCheckIns, previousKeyResults };
    },
    onError: (error: any, variables, context) => {
      console.error('Error creating check-in:', error);

      // Extract error message
      const message = error?.message || 'Erro desconhecido ao criar check-in';
      toast.error(`Erro ao criar check-in: ${message}`);

      // Rollback on error
      if (context?.previousCheckIns) {
        queryClient.setQueryData(['checkIns', variables.keyResultId], context.previousCheckIns);
      }
      if (context?.previousKeyResults) {
        context.previousKeyResults.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (newId, variables) => {
      console.log('✅ [useCheckInMutations] Check-in criado com sucesso:', newId);
      toast.success('Check-in registrado com sucesso!');

      // Invalidate check-ins for this key result
      queryClient.invalidateQueries({ queryKey: ['checkIns', variables.keyResultId] });

      // Invalidate key results to reflect updated progress
      queryClient.invalidateQueries({ queryKey: ['keyResults'] });

      // Invalidate objectives to refresh progress display
      queryClient.invalidateQueries({ queryKey: ['objectives'] });

      // Find and invalidate the objective's cycle
      const keyResultsData = queryClient.getQueriesData({ queryKey: ['keyResults'] });
      keyResultsData.forEach(([, data]: [any, any]) => {
        if (Array.isArray(data)) {
          // Find the key result that matches the check-in
          const kr = data.find((k: any) => {
            const checkIns = queryClient.getQueryData(['checkIns', k.id]);
            return checkIns !== undefined;
          });
          if (kr) {
            queryClient.invalidateQueries({ queryKey: ['objectives', kr.cycleId] });
          }
        }
      });
    },
  });

  return {
    createCheckIn,
  };
}
